<template>
  <div class="profile">
    <div v-if="!isComponentLoading" class="profile__avatar-selection">
      <h3 class="profile__avatar-title">Avatar</h3>
      <div class="profile__avatar-current">
        <p class="profile__avatar-subtitle">Current:</p>
        <img
          v-if="!avatarUpload"
          class="profile__avatar-img profile__avatar-img--current"
          :src="currentAvatar"
        />
        <AvatarPreview v-if="avatarUpload" :avatar="avatarUpload" />
        <div v-if="!isCordova" class="profile__avatar-upload-wrapper">
          <button class="btn__large">Upload File</button>
          <input
            class="profile__avatar-upload"
            type="file"
            placeholder="Avatar"
            accept=".jpg, .jpeg, .png"
            ref="avatarUpload"
            name="file"
            @change="handleAvatarUpload()"
          />
        </div>
        <div
          v-else
          class="profile__avatar-upload-wrapper profile__avatar-upload-wrapper-mobile"
        >
          <button @click="createAvatarFromFile()" class="btn__large">
            Select File
          </button>
          <button @click="createAvatarFromCamera()" class="btn__large">
            Take Picture
          </button>
        </div>
        <span class="profile__upload-info">
          Images may be .jpg and .png only. Max file size is 1MB.
        </span>
      </div>
      <p class="profile__avatar-subtitle">Options:</p>
      <div class="profile__avatar-options">
        <div
          v-for="avatar in sampleAvatars"
          :key="`avatar-${avatar.id}`"
          @click="updateAvatar(avatar.url)"
          class="profile__avatar-option"
        >
          <img
            :alt="avatar.name"
            class="profile__avatar-img"
            :class="
              currentAvatar === avatar.url
                ? 'profile__avatar-img--current-option'
                : ''
            "
            :src="avatar.url"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import AvatarPreview from '@/components/user/user-dashboard/AvatarPreview.vue'

export default {
  name: 'UserDashboardProfile',
  components: {
    AvatarPreview,
  },
  data() {
    return {
      avatarUpload: '',
    }
  },
  computed: {
    ...mapGetters({
      sampleAvatars: 'user/sampleAvatars',
      currentAvatar: 'user/currentAvatar',
      isComponentLoading: 'app/isComponentLoading',
      isCordova: 'app/isCordova',
    }),
  },
  methods: {
    cameraOptions() {
      const options = {
        quality: 5,
        /* eslint-disable */
        destinationType: Camera.DestinationType.FILE_URI,
        mediaType: Camera.MediaType.PICTURE,
        encodingType: Camera.EncodingType.JPEG,
        /* eslint-enable */
        sourceType: '',
        allowEdit: true,
        correctOrientation: true,
        targetWidth: 300,
        targetHeight: 400,
      }
      return options
    },
    async updateAvatar(avatar) {
      this.$store.dispatch('app/setIsComponentLoading', true)
      this.avatarUpload = ''
      const postData = {
        type: 'binary',
        file: avatar,
      }
      await this.$store.dispatch('user/updateUserAvatar', postData)
      this.$store.dispatch('app/setIsComponentLoading', false)
    },
    async handleAvatarUpload() {
      this.$store.dispatch('app/setIsComponentLoading', true)
      this.avatarUpload = this.$refs.avatarUpload.files[0]
      if (this.avatarUpload.size > 1048576) {
        alert('Please select a file that is less than 1mb in size.')
        this.avatarUpload = ''
      } else {
        const postData = {
          type: 'binary',
          file: this.avatarUpload,
        }
        await this.$store.dispatch('user/updateUserAvatar', postData)
      }
      this.$store.dispatch('app/setIsComponentLoading', false)
    },
    async updateAvatarFromCordova(avatar) {
      this.$store.dispatch('app/setIsComponentLoading', true)
      this.avatarUpload = ''
      const postData = {
        type: 'base64',
        file: avatar,
      }
      await this.$store.dispatch('user/updateUserAvatar', postData)
      this.$store.dispatch('app/setIsComponentLoading', false)
    },
    createAvatarFromCamera() {
      const cameraOptions = this.cameraOptions()
      /* eslint-disable */
      cameraOptions.sourceType = Camera.PictureSourceType.CAMERA,
      navigator.camera.getPicture(this.onCameraSuccess, this.onCameraFail, cameraOptions)
      /* eslint-enable */
    },
    async onCameraSuccess(imageData) {
      const picture = await this.processPicture(imageData)
      await this.updateAvatarFromCordova(picture)
    },
    onCameraFail(error) {
      alert(error)
    },
    createAvatarFromFile() {
      const fileOptions = this.cameraOptions()
      /* eslint-disable */
      fileOptions.sourceType = Camera.PictureSourceType.SAVEDPHOTOALBUM,
      navigator.camera.getPicture(this.onCameraSuccess, this.onCameraFail, fileOptions)
      /* eslint-enable */
    },
    getFileEntry(fileURL) {
      return new Promise((resolve, reject) => {
        window.resolveLocalFileSystemURL(
          fileURL,
          fileEntry => {
            resolve(fileEntry)
          },
          error => {
            reject(error)
          }
        )
      })
    },
    readFile(fileEntry) {
      return new Promise((resolve, reject) => {
        fileEntry.file(
          file => {
            const reader = new FileReader()
            reader.readAsDataURL(file)
            reader.onload = () => resolve(reader.result)
            reader.onerror = error => reject(error)
          },
          error => {
            reject(error)
          }
        )
      })
    },
    async processPicture(path) {
      const imgFileEntry = await this.getFileEntry(path)
      const imgFile = await this.readFile(imgFileEntry)
      return imgFile
    },
  },
}
</script>

<style lang="scss" scoped>
.profile {
  $this: &;
  &__avatar-subtitle {
    color: var(--gray);
    text-transform: uppercase;
    font-size: 1rem;
    margin: 0 5px 0 0;
  }
  &__avatar-title {
    color: var(--white);
    margin: 0 0 20px 0;
  }
  &__avatar-img {
    max-height: 100px;
    width: auto;
    border-radius: 100%;
    &--current {
      margin: 0 0 0 10px;
    }
    &--current-option {
      border: 5px solid var(--orange);
    }
  }
  &__avatar-current {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    min-height: 110px;
    margin: 0 0 20px 0;
  }
  &__loading-spinner {
    margin: 0 0 0 46px;
  }
  &__avatar-options {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    margin: 10px 0 0 0;
  }
  &__avatar-option {
    margin: 0 15px 15px 0;
    cursor: pointer;
    &:hover {
      opacity: 0.8;
    }
  }
  &__avatar-upload-wrapper {
    position: relative;
    @include tablet {
      margin: 0 15px;
      width: 150px;
      &:hover {
        .btn__large {
          background: var(--white);
          color: var(--black);
        }
      }
    }
  }
  &__avatar-upload-wrapper-mobile {
    margin: 20px 0;
    button {
      margin: 0 10px;
    }
  }
  &__avatar-upload {
    cursor: pointer;
    opacity: 0;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    appearance: none;
    font-size: 0;
    z-index: 400;
    margin: 0;
  }
  &__upload-info {
    color: var(--white);
    font-size: 1rem;
    font-family: var(--font-bold);
    font-style: italic;
    margin: 0 0 0 auto;
    @include tablet {
      margin: 0;
      width: 220px;
    }
  }
}
</style>
